"use client";

import { ScrollReveal } from "@/components/ui/scroll-reveal";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { ROOT_AUTH_URL, ROOT_PRIORITY_URL } from "@/constants/url";

const AUTH_HREF = `${ROOT_AUTH_URL}/login?redirect_path=${ROOT_PRIORITY_URL}&no_return=0`;

const plans = [
  { name: "Старт", price: "1 990", limit: "до 500К руб/мес", popular: false },
  { name: "Рост", price: "2 990", limit: "до 2 млн руб/мес", popular: true },
  { name: "Бизнес", price: "4 990", limit: "до 5 млн руб/мес", popular: false },
];

const features = [
  "Единый дашборд WB + Ozon",
  "Прибыль по каждому артикулу",
  "Автоматический расчёт себестоимости",
  "Прогноз поставок",
  "Умные алерты и инсайты",
  "Командный доступ",
];

export function Pricing() {
  return (
    <section id="pricing" className="py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <ScrollReveal>
          <p className="text-sm font-medium text-purple-400 text-center mb-4">
            Тарифы
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight text-center">
            Дешевле одного
            <br />
            <span className="text-white/50">убыточного артикула в месяц</span>
          </h2>
          <p className="text-base text-white/50 text-center mt-4 max-w-xl mx-auto">
            Все тарифы включают полный функционал. Разница только в обороте вашего магазина.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16">
          {plans.map((plan, index) => (
            <ScrollReveal key={plan.name} delay={index * 0.1}>
              <div
                className={cn(
                  "rounded-2xl p-8 flex flex-col h-full border transition-all duration-300",
                  plan.popular
                    ? "bg-white text-black border-white/20 shadow-[0_0_60px_rgba(168,85,247,0.15)]"
                    : "bg-white/[0.03] text-white border-white/[0.08] hover:bg-white/[0.06]"
                )}
              >
                {/* Название */}
                <p
                  className={cn(
                    "text-sm font-medium",
                    plan.popular ? "text-gray-500" : "text-white/50"
                  )}
                >
                  {plan.name}
                </p>

                {/* Цена */}
                <div className="mt-4 flex items-baseline gap-1">
                  <span
                    className={cn(
                      "text-5xl font-bold tracking-tight",
                      plan.popular ? "text-black" : "text-white"
                    )}
                  >
                    ₽{plan.price}
                  </span>
                  <span
                    className={cn(
                      "text-sm",
                      plan.popular ? "text-gray-400" : "text-white/50"
                    )}
                  >
                    /мес
                  </span>
                </div>

                {/* Лимит */}
                <p
                  className={cn(
                    "text-sm font-medium mt-2",
                    plan.popular ? "text-purple-600" : "text-purple-400"
                  )}
                >
                  {plan.limit}
                </p>

                {/* Разделитель */}
                <div
                  className={cn(
                    "h-px my-6",
                    plan.popular ? "bg-gray-200" : "bg-white/[0.06]"
                  )}
                />

                {/* Функции */}
                <ul className="space-y-3 flex-1">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check
                        className={cn(
                          "w-4 h-4 mt-0.5 shrink-0",
                          plan.popular ? "text-purple-600" : "text-purple-400"
                        )}
                      />
                      <span
                        className={cn(
                          "text-sm",
                          plan.popular ? "text-gray-600" : "text-white/50"
                        )}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  href={AUTH_HREF}
                  className={cn(
                    "mt-8 w-full py-3 rounded-xl text-sm font-semibold text-center block transition-all duration-200",
                    plan.popular
                      ? "bg-black text-white hover:bg-gray-800"
                      : "bg-white/[0.06] text-white/70 border border-white/[0.08] hover:bg-white/[0.1]"
                  )}
                >
                  Начать бесплатно
                </Link>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={0.3}>
          <p className="text-sm text-white/60 text-center mt-10">
            30 дней бесплатно, без привязки карты
          </p>
          <p className="text-xs text-white/40 text-center mt-2">
            Оплата: СБП, банковские карты, ЮMoney
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
